import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import {
  FiSave, FiPackage, FiCreditCard, FiPercent, FiTag,
  FiFileText, FiX, FiPlus, FiImage, FiBarChart
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import styles from './ProductCreate.module.css';

interface ProductForm {
  name: string;
  description: string;
  sku: string;
  price: number;
  cost: number;
  taxRate: number;
  category: string;
  unit: string;
  stockQuantity?: number;
  minStockLevel?: number;
  isActive: boolean;
  tags: string[];
  notes: string;
}

export function ProductCreate() {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<ProductForm>({
    defaultValues: {
      isActive: true,
      tags: [],
      taxRate: 0,
      price: 0,
      cost: 0,
      unit: 'piece'
    }
  });

  const watchedTags = watch('tags');
  const watchedPrice = watch('price');
  const watchedCost = watch('cost');

  const profit = watchedPrice - watchedCost;
  const profitMargin = watchedPrice > 0 ? (profit / watchedPrice) * 100 : 0;

  const addTag = () => {
    if (newTag.trim() && !watchedTags.includes(newTag.trim())) {
      setValue('tags', [...watchedTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue('tags', watchedTags.filter(tag => tag !== tagToRemove));
  };

  const onSubmit = async (data: ProductForm) => {
    try {
      setIsSaving(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('New product:', { ...data, id: uuidv4() });
      toast.success('Product created successfully!');
      navigate('/products');
    } catch (error) {
      toast.error('Failed to create product');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div 
      className={styles.container} 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button 
            onClick={() => navigate('/products')}
            className={styles.backButton}
          >
            <FiX size={20} />
            Cancel
          </button>
          <h1 className={styles.title}>Add New Product/Service</h1>
          <p className={styles.subtitle}>Create a new product or service with pricing and inventory details</p>
        </div>
        
        <div className={styles.headerActions}>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isSaving || !isValid}
            className={`${styles.actionButton} ${styles.saveButton} ${isSaving ? styles.loading : ''}`}
          >
            <FiSave size={16} />
            {isSaving ? 'Creating...' : 'Create Product'}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          {/* Basic Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiPackage size={20} />
              Basic Information
            </h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Product/Service Name *</label>
                <input
                  {...register('name', { required: 'Product name is required' })}
                  className={`${styles.input} ${errors.name ? styles.error : ''}`}
                  placeholder="Enter product or service name"
                />
                {errors.name && (
                  <span className={styles.errorMessage}>{errors.name.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>SKU</label>
                <input
                  {...register('sku')}
                  className={styles.input}
                  placeholder="Enter SKU (Stock Keeping Unit)"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Description</label>
              <textarea
                {...register('description')}
                className={styles.textarea}
                placeholder="Enter detailed description"
                rows={3}
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Category</label>
                <select
                  {...register('category')}
                  className={styles.select}
                >
                  <option value="">Select category</option>
                  <option value="websites">Websites</option>
                  <option value="ecommerce">eCommerce</option>
                  <option value="hosting">Hosting</option>
                  <option value="domains">Domains</option>
                  <option value="services">Services</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Unit</label>
                <select
                  {...register('unit')}
                  className={styles.select}
                >
                  <option value="project">Project</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                  <option value="piece">Piece</option>
                  <option value="kg">Kilogram</option>
                  <option value="liter">Liter</option>
                  <option value="meter">Meter</option>
                  <option value="set">Set</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiCreditCard size={20} />
              Pricing Information
            </h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Selling Price *</label>
                <div className={styles.inputWrapper}>
                  <FiCreditCard className={styles.inputIcon} />
                  <input
                    type="number"
                    {...register('price', { 
                      required: 'Price is required',
                      min: { value: 0, message: 'Price must be positive' }
                    })}
                    className={`${styles.input} ${errors.price ? styles.error : ''}`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                {errors.price && (
                  <span className={styles.errorMessage}>{errors.price.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Cost Price</label>
                <div className={styles.inputWrapper}>
                  <FiCreditCard className={styles.inputIcon} />
                  <input
                    type="number"
                    {...register('cost', { 
                      min: { value: 0, message: 'Cost must be positive' }
                    })}
                    className={`${styles.input} ${errors.cost ? styles.error : ''}`}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                {errors.cost && (
                  <span className={styles.errorMessage}>{errors.cost.message}</span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tax Rate (%)</label>
                <div className={styles.inputWrapper}>
                  <FiPercent className={styles.inputIcon} />
                  <input
                    type="number"
                    {...register('taxRate', { 
                      min: { value: 0, message: 'Tax rate must be positive' },
                      max: { value: 100, message: 'Tax rate cannot exceed 100%' }
                    })}
                    className={`${styles.input} ${errors.taxRate ? styles.error : ''}`}
                    placeholder="0.00"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                {errors.taxRate && (
                  <span className={styles.errorMessage}>{errors.taxRate.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Status</label>
                <select
                  {...register('isActive')}
                  className={styles.select}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>

            {/* Profit Analysis */}
            {watchedPrice > 0 && (
              <div className={styles.profitAnalysis}>
                <h3 className={styles.profitTitle}>
                                      <FiBarChart size={16} />
                  Profit Analysis
                </h3>
                <div className={styles.profitGrid}>
                  <div className={styles.profitItem}>
                    <span className={styles.profitLabel}>Profit:</span>
                    <span className={`${styles.profitValue} ${profit >= 0 ? styles.positive : styles.negative}`}>
                      ${profit.toFixed(2)}
                    </span>
                  </div>
                  <div className={styles.profitItem}>
                    <span className={styles.profitLabel}>Profit Margin:</span>
                    <span className={`${styles.profitValue} ${profitMargin >= 0 ? styles.positive : styles.negative}`}>
                      {profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Inventory Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiPackage size={20} />
              Inventory Information
            </h2>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Stock Quantity</label>
                <input
                  type="number"
                  {...register('stockQuantity', { 
                    min: { value: 0, message: 'Stock quantity must be positive' }
                  })}
                  className={`${styles.input} ${errors.stockQuantity ? styles.error : ''}`}
                  placeholder="Enter stock quantity"
                  min="0"
                  step="1"
                />
                {errors.stockQuantity && (
                  <span className={styles.errorMessage}>{errors.stockQuantity.message}</span>
                )}
              </div>
              
              <div className={styles.formGroup}>
                <label className={styles.label}>Minimum Stock Level</label>
                <input
                  type="number"
                  {...register('minStockLevel', { 
                    min: { value: 0, message: 'Minimum stock level must be positive' }
                  })}
                  className={`${styles.input} ${errors.minStockLevel ? styles.error : ''}`}
                  placeholder="Enter minimum stock level"
                  min="0"
                  step="1"
                />
                {errors.minStockLevel && (
                  <span className={styles.errorMessage}>{errors.minStockLevel.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiFileText size={20} />
              Additional Information
            </h2>
            
            <div className={styles.formGroup}>
              <label className={styles.label}>Notes</label>
              <textarea
                {...register('notes')}
                className={styles.textarea}
                placeholder="Add any additional notes about this product"
                rows={4}
              />
            </div>
          </div>

          {/* Tags */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>
              <FiTag size={20} />
              Tags
            </h2>
            
            <div className={styles.tagsSection}>
              <div className={styles.addTagForm}>
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  className={styles.tagInput}
                  placeholder="Add a tag"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <button
                  type="button"
                  onClick={addTag}
                  className={styles.addTagButton}
                  disabled={!newTag.trim()}
                >
                  <FiPlus size={16} />
                </button>
              </div>

              {watchedTags.length > 0 && (
                <div className={styles.tagsList}>
                  {watchedTags.map((tag, index) => (
                    <div key={index} className={styles.tag}>
                      <span className={styles.tagText}>{tag}</span>
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className={styles.removeTagButton}
                      >
                        <FiX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
